import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Transaction } from '@solana/web3.js';
import bs58 from 'bs58';

import { useWalletAddress } from '@/hooks/useWalletAddress';
import { getConnection } from '@/lib/solana/anchorClient';
import * as chestApi from '@/api/chest';
import { getGenericErrorMessage } from '@/lib/logger';
import { isMobileWallet } from '@/lib/responsive';
import { captureException, createErrorContext } from '@/lib/sentry';
import { parseSolanaError, isUserActionableError } from '@/utils/solanaErrorParser';

// Chest reward with bigint for amount
export interface ChestReward {
  tierIndex: number;
  amount: bigint;
  amountSol: number;
}

export interface PointReward {
  tierIndex: number;
  amount: number;
}

interface OpenChestInput {
  chestCount: number;
  onProgress?: (step: 'signing' | 'sending' | 'processing' | 'confirming', attempt?: number) => void;
}

interface OpenChestResult {
  signatures: string[];
  chestsOpened: number;
  rewards: ChestReward[];
  totalPayout: bigint;
  totalPayoutSol: number;
  tierPointsEarned: number;
  pointRewards: PointReward[];
}

async function openChest(
  chestCount: number,
  walletProvider: any,
  onProgress?: (step: 'signing' | 'sending' | 'processing' | 'confirming', attempt?: number) => void
): Promise<OpenChestResult> {
  const walletAddr = walletProvider.publicKey?.toString() || 'unknown';
  const errorContext = createErrorContext('open_chest', walletAddr, { chestCount });

  if (chestCount <= 0) {
    const err = new Error('Chest count must be greater than 0');
    captureException(err, errorContext);
    throw err;
  }

  const addrShort = walletAddr.length > 8 ? `${walletAddr.slice(0, 5)}...${walletAddr.slice(-4)}` : walletAddr;
  const connection = getConnection();
  const signatures: string[] = [];
  let totalChestsOpened = 0;
  const allRewards: ChestReward[] = [];
  let totalPayout = BigInt(0);
  let totalTierPoints = 0;
  const allPointRewards: PointReward[] = [];

  const isMobile = isMobileWallet();
  console.log(`[OpenChest] Platform: ${isMobile ? 'Mobile' : 'Desktop'}, Chests: ${chestCount}`);

  if (isMobile) {
    // MOBILE FLOW: Create orders → Get signatures (just-in-time) → Sign all → Send all
    // This prevents blockhash expiration while maintaining good UX (single wallet popup)
    console.log('[OpenChest:Mobile] Using just-in-time batch signing flow');

    onProgress?.('processing');
    const ordersResponse = await chestApi.createChestOrders(chestCount);
    if (ordersResponse.code !== 0 || !ordersResponse.data || ordersResponse.data.length === 0) {
      const err = new Error(ordersResponse.message || 'Failed to create chest orders');
      captureException(err, { ...errorContext, stage: 'mobile_create_orders', responseCode: ordersResponse.code });
      throw err;
    }

    const orderIds = ordersResponse.data;
    console.log(`[OpenChest:Mobile] Created ${orderIds.length} orders, getting fresh signatures...`);

    // Get all signatures just-in-time (right before user signs)
    const signaturesResponse = await chestApi.generateSignatures(orderIds);
    if (signaturesResponse.code !== 0 || !signaturesResponse.data) {
      const err = new Error(signaturesResponse.message || 'Failed to get signatures');
      captureException(err, { ...errorContext, stage: 'mobile_get_signatures', orderIds, responseCode: signaturesResponse.code });
      throw err;
    }

    const txData = signaturesResponse.data.signatures;

    // Deserialize all transactions (backend sends Legacy Transactions)
    const transactions: Transaction[] = [];
    for (const { serialized_tx } of txData) {
      const txBuffer = Buffer.from(serialized_tx, 'hex');
      const transaction = Transaction.from(txBuffer);
      transactions.push(transaction);
    }

    // Store original signatures before signing (for wallets that corrupt them like Gate)
    const originalSignaturesMobile: { pubkey: string; signature: Buffer | null }[][] = [];
    for (const tx of transactions) {
      const sigs: { pubkey: string; signature: Buffer | null }[] = [];
      for (const sig of tx.signatures) {
        if (sig.signature) {
          sigs.push({ pubkey: sig.publicKey.toBase58(), signature: Buffer.from(sig.signature) });
        }
      }
      originalSignaturesMobile.push(sigs);
    }

    console.log(`[OpenChest:Mobile] All ${transactions.length} transactions ready with fresh blockhashes`);

    // Sign all transactions at once (single wallet popup)
    onProgress?.('signing');
    console.log(`[OpenChest:Mobile] Requesting user to sign all ${transactions.length} transactions...`);
    const signedTransactions = await walletProvider.signAllTransactions(transactions);
    console.log(`[OpenChest:Mobile] User signed all ${transactions.length} transactions`);

    // Restore any missing or corrupted signatures (Gate Wallet via AppKit may overwrite backend signatures)
    for (let i = 0; i < signedTransactions.length; i++) {
      const signedTx = signedTransactions[i] as Transaction;
      const origSigs = originalSignaturesMobile[i];

      for (const origSig of origSigs) {
        const existingSig = signedTx.signatures.find(
          (s: { publicKey: { toBase58: () => string }; signature: Buffer | null }) => s.publicKey.toBase58() === origSig.pubkey
        );

        if (existingSig && origSig.signature) {
          const currentSigHex = existingSig.signature ? Buffer.from(existingSig.signature).toString('hex') : null;
          const originalSigHex = origSig.signature.toString('hex');

          if (!existingSig.signature || currentSigHex !== originalSigHex) {
            console.log(`[OpenChest:Mobile] Restoring signature for ${origSig.pubkey.slice(0, 8)}... (was ${currentSigHex ? 'corrupted' : 'missing'})`);
            existingSig.signature = origSig.signature;
          }
        }
      }
    }

    // Send each signed transaction
    onProgress?.('sending');
    for (let i = 0; i < signedTransactions.length; i++) {
      const signedTx = signedTransactions[i];
      const orderId = txData[i].order_id;
      try {
        const signature = await connection.sendRawTransaction(signedTx.serialize(), {
          skipPreflight: false,
          preflightCommitment: 'confirmed',
        });

        // Wait for confirmation with retry on rate limiting
        let confirmationAttempts = 0;
        let confirmed = false;

        while (!confirmed && confirmationAttempts < 5) {
          try {
            const latestBlockhash = await connection.getLatestBlockhash();
            await connection.confirmTransaction({
              signature,
              blockhash: latestBlockhash.blockhash,
              lastValidBlockHeight: latestBlockhash.lastValidBlockHeight
            }, 'confirmed');
            confirmed = true;
          } catch (confirmError: any) {
            confirmationAttempts++;
            const errorMsg = confirmError?.message || '';

            if (errorMsg.includes('429') || errorMsg.includes('Too many requests')) {
              console.log(`[OpenChest:Mobile] Rate limited on confirmation attempt ${confirmationAttempts}, waiting before retry...`);
              const delay = Math.min(1000 * Math.pow(2, confirmationAttempts - 1), 8000);
              await new Promise(resolve => setTimeout(resolve, delay));
              continue;
            }

            if (confirmationAttempts >= 5) {
              throw confirmError;
            }
          }
        }

        signatures.push(signature);

        const sigShort = `${signature.slice(0, 8)}...${signature.slice(-8)}`;
        console.log(`[OpenChest:Mobile] ${sigShort} addr:${addrShort} order:${orderId} transaction confirmed, polling backend for rewards...`);

        // Poll backend API for order details with rewards
        onProgress?.('confirming');
        const orderDetail = await chestApi.pollOrderUntilConfirmed(orderId, 30, 1000, (attempt) => {
          onProgress?.('confirming', attempt);
        });

        // Extract rewards from backend metadata
        const chestsOpened = orderDetail.metadata.chest_count || 0;
        const rewardAmounts = orderDetail.metadata.reward_amounts || [];
        const rewardIndexes = orderDetail.metadata.reward_indexes || [];
        const isPoints = orderDetail.metadata.isPoints || [];

        totalChestsOpened += chestsOpened;

        let orderTierPoints = 0;
        let orderSolPayout = BigInt(0);

        for (let j = 0; j < rewardAmounts.length; j++) {
          const amount = rewardAmounts[j];
          const tierIndex = rewardIndexes[j] ?? 255;
          const isPoint = isPoints[j] ?? false;

          if (tierIndex === 255) continue;

          if (isPoint) {
            orderTierPoints += amount;
            allPointRewards.push({ tierIndex, amount });
          } else {
            const amountBigInt = BigInt(amount);
            orderSolPayout += amountBigInt;
            allRewards.push({
              tierIndex,
              amount: amountBigInt,
              amountSol: Number(amountBigInt) / 1e9,
            });
          }
        }

        totalTierPoints += orderTierPoints;
        totalPayout += orderSolPayout;

        console.log(`[OpenChest:Mobile] ${sigShort} addr:${addrShort} order:${orderId} chest:${chestsOpened} extraPoint:${orderTierPoints} solRewards:${orderSolPayout.toString()}`);

      } catch (error: any) {
        const errorMessage = error?.message || String(error);
        const isAlreadyProcessed = errorMessage.includes('already been processed') ||
                                   errorMessage.includes('This transaction has already been processed') ||
                                   errorMessage.includes('already processed') ||
                                   errorMessage.toLowerCase().includes('transaction has already been processed');

        if (isAlreadyProcessed) {
          const signature = bs58.encode(signedTx.signature);

          try {
            const status = await connection.getSignatureStatus(signature);

            if (status?.value?.confirmationStatus &&
                (status.value.confirmationStatus === 'confirmed' || status.value.confirmationStatus === 'finalized') &&
                !status.value.err) {
              signatures.push(signature);

              const sigShort = `${signature.slice(0, 8)}...${signature.slice(-8)}`;
              console.log(`[OpenChest:Mobile:AlreadyProcessed] ${sigShort} addr:${addrShort} order:${orderId} transaction already confirmed, polling backend for rewards...`);

              onProgress?.('confirming');
              const orderDetail = await chestApi.pollOrderUntilConfirmed(orderId, 30, 1000, (attempt) => {
                onProgress?.('confirming', attempt);
              });

              const chestsOpened = orderDetail.metadata.chest_count || 0;
              const rewardAmounts = orderDetail.metadata.reward_amounts || [];
              const rewardIndexes = orderDetail.metadata.reward_indexes || [];
              const isPoints = orderDetail.metadata.isPoints || [];

              totalChestsOpened += chestsOpened;

              let orderTierPoints = 0;
              let orderSolPayout = BigInt(0);

              for (let j = 0; j < rewardAmounts.length; j++) {
                const amount = rewardAmounts[j];
                const tierIndex = rewardIndexes[j] ?? 255;
                const isPoint = isPoints[j] ?? false;

                if (tierIndex === 255) continue;

                if (isPoint) {
                  orderTierPoints += amount;
                  allPointRewards.push({ tierIndex, amount });
                } else {
                  const amountBigInt = BigInt(amount);
                  orderSolPayout += amountBigInt;
                  allRewards.push({
                    tierIndex,
                    amount: amountBigInt,
                    amountSol: Number(amountBigInt) / 1e9,
                  });
                }
              }

              totalTierPoints += orderTierPoints;
              totalPayout += orderSolPayout;

              console.log(`[OpenChest:Mobile:AlreadyProcessed] ${sigShort} addr:${addrShort} order:${orderId} chest:${chestsOpened} extraPoint:${orderTierPoints} solRewards:${orderSolPayout.toString()}`);
              continue;
            }
          } catch (statusError: any) {
            const statusErrorMsg = statusError?.message || '';
            if (!statusErrorMsg.includes('429') && !statusErrorMsg.includes('Too many requests')) {
              console.debug('Could not verify transaction status:', statusError);
            }
          }
        }

        // Check for blockhash or rate limit errors - return partial success if we've opened some chests
        const isBlockhashError = errorMessage.includes('Blockhash not found') ||
                                errorMessage.includes('blockhash') ||
                                errorMessage.includes('expired');
        const isRateLimitError = errorMessage.includes('429') || errorMessage.includes('Too many requests');

        if (isBlockhashError) {
          console.warn(`[OpenChest:Mobile] Blockhash expired after opening ${totalChestsOpened} chests (tx ${i + 1}/${signedTransactions.length})`);
          if (totalChestsOpened > 0) {
            break;
          }
        }

        if (isRateLimitError) {
          console.warn(`[OpenChest:Mobile] Rate limited, continuing with partial results after tx ${i + 1}/${signedTransactions.length}`);
          if (totalChestsOpened > 0) {
            break;
          }
        }

        if (isAlreadyProcessed) {
          console.log(`[OpenChest:Mobile] Transaction already processed but couldn't verify status. Continuing...`);
          continue;
        }

        if (!isAlreadyProcessed && !isBlockhashError && !isRateLimitError) {
          console.error('Transaction failed:', error);
          captureException(error, {
            ...errorContext,
            stage: 'mobile_send_transaction',
            transactionIndex: i + 1,
            transactionCount: signedTransactions.length,
            completedChests: totalChestsOpened,
            completedSignatures: signatures,
          });
        }

        if (totalChestsOpened === 0 && !isAlreadyProcessed) {
          throw error;
        }

        if (!isAlreadyProcessed) {
          console.warn(`[OpenChest:Mobile] Continuing with partial results after error on tx ${i + 1}/${signedTransactions.length}`);
          break;
        }
      }
    }

    console.log(`[OpenChest:Mobile] Completed ${totalChestsOpened} chests with ${signatures.length} transactions`);
  } else {
    // DESKTOP FLOW: Create all orders → Get all signatures → Sign all → Send all
    console.log('[OpenChest] Using desktop batch signing flow');

    // Step 1: Create orders on backend
    onProgress?.('processing');
    const ordersResponse = await chestApi.createChestOrders(chestCount);
    if (ordersResponse.code !== 0 || !ordersResponse.data || ordersResponse.data.length === 0) {
      const err = new Error(ordersResponse.message || 'Failed to create chest orders');
      captureException(err, { ...errorContext, stage: 'desktop_create_orders', responseCode: ordersResponse.code });
      throw err;
    }

    const orderIds = ordersResponse.data;

    // Step 2: Get pre-signed transactions from backend
    const signaturesResponse = await chestApi.generateSignatures(orderIds);
    if (signaturesResponse.code !== 0 || !signaturesResponse.data) {
      const err = new Error(signaturesResponse.message || 'Failed to get signatures');
      captureException(err, { ...errorContext, stage: 'desktop_get_signatures', orderIds, responseCode: signaturesResponse.code });
      throw err;
    }

    const txData = signaturesResponse.data.signatures;

    // Step 3: Deserialize all transactions first (backend sends Legacy Transactions)
    const transactions: Transaction[] = [];

    for (const { serialized_tx } of txData) {
      const txBuffer = Buffer.from(serialized_tx, 'hex');
      const transaction = Transaction.from(txBuffer);
      transactions.push(transaction);
    }

    // Debug: Log signatures before signing
    console.log('[OpenChest:Debug] Wallet provider type:', walletProvider?.isGateWallet ? 'GateWallet' : walletProvider?.isPhantom ? 'Phantom' : 'AppKit');
    console.log('[OpenChest:Debug] Transactions before signing:');
    for (let i = 0; i < transactions.length; i++) {
      const tx = transactions[i];
      const sigDetails = tx.signatures.map(s => ({
        pubkey: s.publicKey.toBase58(),
        hasSig: !!s.signature,
        sigLen: s.signature?.length,
        sigFirst8: s.signature ? Buffer.from(s.signature).toString('hex').slice(0, 16) : null
      }));
      console.log(`[OpenChest:Debug] Tx ${i} signatures before:`, JSON.stringify(sigDetails, null, 2));
      console.log(`[OpenChest:Debug] Tx ${i} recentBlockhash before:`, tx.recentBlockhash);
      console.log(`[OpenChest:Debug] Tx ${i} feePayer before:`, tx.feePayer?.toBase58());
    }

    // Store original signatures before signing (for wallets that don't preserve them)
    const originalSignatures: { pubkey: string; signature: Buffer | null }[][] = [];
    for (const tx of transactions) {
      const sigs: { pubkey: string; signature: Buffer | null }[] = [];
      for (const sig of tx.signatures) {
        if (sig.signature) {
          sigs.push({ pubkey: sig.publicKey.toBase58(), signature: Buffer.from(sig.signature) });
        }
      }
      originalSignatures.push(sigs);
    }

    // Step 4: Sign all transactions at once (single wallet popup)
    onProgress?.('signing');
    const signedTransactions = await walletProvider.signAllTransactions(transactions);

    // Debug: Log signatures after signing
    console.log('[OpenChest:Debug] Transactions after signing:');
    for (let i = 0; i < signedTransactions.length; i++) {
      const tx = signedTransactions[i] as Transaction;
      const sigDetails = tx.signatures.map((s: { publicKey: { toBase58: () => string }; signature: Buffer | null }) => ({
        pubkey: s.publicKey.toBase58(),
        hasSig: !!s.signature,
        sigLen: s.signature?.length,
        sigFirst8: s.signature ? Buffer.from(s.signature).toString('hex').slice(0, 16) : null
      }));
      console.log(`[OpenChest:Debug] Tx ${i} signatures after:`, JSON.stringify(sigDetails, null, 2));
      console.log(`[OpenChest:Debug] Tx ${i} recentBlockhash after:`, tx.recentBlockhash);
      console.log(`[OpenChest:Debug] Tx ${i} feePayer after:`, tx.feePayer?.toBase58());

      // Compare with original
      const origSigs = originalSignatures[i];
      for (const origSig of origSigs) {
        const matchingSig = tx.signatures.find((s: { publicKey: { toBase58: () => string }; signature: Buffer | null }) =>
          s.publicKey.toBase58() === origSig.pubkey
        );
        if (matchingSig && matchingSig.signature && origSig.signature) {
          const origHex = origSig.signature.toString('hex').slice(0, 16);
          const newHex = Buffer.from(matchingSig.signature).toString('hex').slice(0, 16);
          const sigChanged = origHex !== newHex;
          console.log(`[OpenChest:Debug] Tx ${i} sig for ${origSig.pubkey.slice(0, 8)}... changed: ${sigChanged} (before: ${origHex}, after: ${newHex})`);
        }
      }
    }

    // Restore any missing or corrupted signatures (Gate Wallet may overwrite backend signatures)
    for (let i = 0; i < signedTransactions.length; i++) {
      const signedTx = signedTransactions[i] as Transaction;
      const origSigs = originalSignatures[i];

      for (const origSig of origSigs) {
        const existingSig = signedTx.signatures.find(
          (s: { publicKey: { toBase58: () => string }; signature: Buffer | null }) => s.publicKey.toBase58() === origSig.pubkey
        );

        if (existingSig && origSig.signature) {
          // Check if signature is missing OR if it was changed/corrupted
          const currentSigHex = existingSig.signature ? Buffer.from(existingSig.signature).toString('hex') : null;
          const originalSigHex = origSig.signature.toString('hex');

          if (!existingSig.signature || currentSigHex !== originalSigHex) {
            console.log(`[OpenChest:Debug] Restoring signature for ${origSig.pubkey.slice(0, 8)}... (was ${currentSigHex ? 'corrupted' : 'missing'})`);
            existingSig.signature = origSig.signature;
          }
        }
      }
    }

    // Debug: Log signatures after restoration
    console.log('[OpenChest:Debug] Transactions after signature restoration:');
    for (let i = 0; i < signedTransactions.length; i++) {
      const tx = signedTransactions[i] as Transaction;
      const sigDetails = tx.signatures.map((s: { publicKey: { toBase58: () => string }; signature: Buffer | null }) => ({
        pubkey: s.publicKey.toBase58(),
        hasSig: !!s.signature,
        sigLen: s.signature?.length,
        sigFirst8: s.signature ? Buffer.from(s.signature).toString('hex').slice(0, 16) : null
      }));
      console.log(`[OpenChest:Debug] Tx ${i} signatures final:`, JSON.stringify(sigDetails, null, 2));
    }

    // Step 5: Send each signed transaction to blockchain and poll backend for rewards
    onProgress?.('sending');
    for (let i = 0; i < signedTransactions.length; i++) {
      const signedTx = signedTransactions[i];
      const orderId = txData[i].order_id; // Get orderId for backend API

    try {
      const signature = await connection.sendRawTransaction(signedTx.serialize(), {
        skipPreflight: false,
        preflightCommitment: 'confirmed',
      });

      // Wait for confirmation with retry on rate limiting
      let confirmationAttempts = 0;
      let confirmed = false;

      while (!confirmed && confirmationAttempts < 5) {
        try {
          // Get latest blockhash for confirmation
          const latestBlockhash = await connection.getLatestBlockhash();
          await connection.confirmTransaction({
            signature,
            blockhash: latestBlockhash.blockhash,
            lastValidBlockHeight: latestBlockhash.lastValidBlockHeight
          }, 'confirmed');
          confirmed = true;
        } catch (confirmError: any) {
          confirmationAttempts++;
          const errorMsg = confirmError?.message || '';

          // Check if it's rate limiting (429)
          if (errorMsg.includes('429') || errorMsg.includes('Too many requests')) {
            console.log(`[OpenChest] Rate limited on confirmation attempt ${confirmationAttempts}, waiting before retry...`);
            // Exponential backoff for rate limiting
            const delay = Math.min(1000 * Math.pow(2, confirmationAttempts - 1), 8000);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }

          // If not rate limiting and we've tried enough times, throw the error
          if (confirmationAttempts >= 5) {
            throw confirmError;
          }
        }
      }

      signatures.push(signature);

      const sigShort = `${signature.slice(0, 8)}...${signature.slice(-8)}`;
      console.log(`[OpenChest] ${sigShort} addr:${addrShort} order:${orderId} transaction confirmed, polling backend for rewards...`);

      // Poll backend API for order details with rewards
      // Backend sync service runs every 10 seconds, so we poll with 1 second intervals
      onProgress?.('confirming');
      const orderDetail = await chestApi.pollOrderUntilConfirmed(orderId, 30, 1000, (attempt) => {
        onProgress?.('confirming', attempt);
      });

      // Extract rewards from backend metadata
      const chestsOpened = orderDetail.metadata.chest_count || 0;
      const rewardAmounts = orderDetail.metadata.reward_amounts || [];
      const rewardIndexes = orderDetail.metadata.reward_indexes || [];
      const isPoints = orderDetail.metadata.isPoints || [];

      // Accumulate totals
      totalChestsOpened += chestsOpened;

      // Separate SOL rewards from point rewards
      let orderTierPoints = 0;
      let orderSolPayout = BigInt(0);

      for (let j = 0; j < rewardAmounts.length; j++) {
        const amount = rewardAmounts[j];
        const tierIndex = rewardIndexes[j] ?? 255;
        const isPoint = isPoints[j] ?? false;

        // Skip "no reward" (tierIndex 255)
        if (tierIndex === 255) continue;

        if (isPoint) {
          // Points reward
          orderTierPoints += amount;
          allPointRewards.push({ tierIndex, amount });
        } else {
          // SOL reward (in lamports)
          const amountBigInt = BigInt(amount);
          orderSolPayout += amountBigInt;
          allRewards.push({
            tierIndex,
            amount: amountBigInt,
            amountSol: Number(amountBigInt) / 1e9,
          });
        }
      }

      totalTierPoints += orderTierPoints;
      totalPayout += orderSolPayout;

      console.log(`[OpenChest] ${sigShort} addr:${addrShort} order:${orderId} chest:${chestsOpened} extraPoint:${orderTierPoints} solRewards:${orderSolPayout.toString()}`);

    } catch (error: any) {
      // Check if this is a "transaction already processed" error
      const errorMessage = error?.message || String(error);
      const isAlreadyProcessed = errorMessage.includes('already been processed') ||
                                 errorMessage.includes('This transaction has already been processed') ||
                                 errorMessage.includes('already processed') ||
                                 errorMessage.toLowerCase().includes('transaction has already been processed');

      if (isAlreadyProcessed) {
        // Get signature from signed transaction
        const signature = bs58.encode(signedTx.signature);

        try {
          // Check if transaction actually succeeded
          const status = await connection.getSignatureStatus(signature);

          if (status?.value?.confirmationStatus &&
              (status.value.confirmationStatus === 'confirmed' || status.value.confirmationStatus === 'finalized') &&
              !status.value.err) {
            // Transaction succeeded!
            signatures.push(signature);

            const sigShort = `${signature.slice(0, 8)}...${signature.slice(-8)}`;
            console.log(`[OpenChest:AlreadyProcessed] ${sigShort} addr:${addrShort} order:${orderId} transaction already confirmed, polling backend for rewards...`);

            // Poll backend API for order details with rewards
            onProgress?.('confirming');
            const orderDetail = await chestApi.pollOrderUntilConfirmed(orderId, 30, 1000, (attempt) => {
              onProgress?.('confirming', attempt);
            });

            // Extract rewards from backend metadata
            const chestsOpened = orderDetail.metadata.chest_count || 0;
            const rewardAmounts = orderDetail.metadata.reward_amounts || [];
            const rewardIndexes = orderDetail.metadata.reward_indexes || [];
            const isPoints = orderDetail.metadata.isPoints || [];

            // Accumulate totals
            totalChestsOpened += chestsOpened;

            // Separate SOL rewards from point rewards
            let orderTierPoints = 0;
            let orderSolPayout = BigInt(0);

            for (let j = 0; j < rewardAmounts.length; j++) {
              const amount = rewardAmounts[j];
              const tierIndex = rewardIndexes[j] ?? 255;
              const isPoint = isPoints[j] ?? false;

              // Skip "no reward" (tierIndex 255)
              if (tierIndex === 255) continue;

              if (isPoint) {
                // Points reward
                orderTierPoints += amount;
                allPointRewards.push({ tierIndex, amount });
              } else {
                // SOL reward (in lamports)
                const amountBigInt = BigInt(amount);
                orderSolPayout += amountBigInt;
                allRewards.push({
                  tierIndex,
                  amount: amountBigInt,
                  amountSol: Number(amountBigInt) / 1e9,
                });
              }
            }

            totalTierPoints += orderTierPoints;
            totalPayout += orderSolPayout;

            console.log(`[OpenChest:AlreadyProcessed] ${sigShort} addr:${addrShort} order:${orderId} chest:${chestsOpened} extraPoint:${orderTierPoints} solRewards:${orderSolPayout.toString()}`);

            continue; // Move to next transaction
          }
        } catch (statusError: any) {
          // Silence rate limiting errors during status check
          const statusErrorMsg = statusError?.message || '';
          if (!statusErrorMsg.includes('429') && !statusErrorMsg.includes('Too many requests')) {
            console.debug('Could not verify transaction status:', statusError);
          }
        }
      }

      // Check if this is a blockhash expiration error
      const isBlockhashError = errorMessage.includes('Blockhash not found') ||
                              errorMessage.includes('blockhash') ||
                              errorMessage.includes('expired');

      if (isBlockhashError) {
        console.warn(`[OpenChest] Blockhash expired after opening ${totalChestsOpened} chests (batch ${i + 1}/${signedTransactions.length})`);
        // If we've already opened some chests, return partial success
        if (totalChestsOpened > 0) {
          break; // Exit loop and return partial results
        }
      }

      // Check if this is rate limiting error (should be rare here since we handle it above)
      const isRateLimitError = errorMessage.includes('429') || errorMessage.includes('Too many requests');
      if (isRateLimitError) {
        console.warn(`[OpenChest] Rate limited, continuing with partial results after batch ${i + 1}/${signedTransactions.length}`);
        if (totalChestsOpened > 0) {
          break; // Return partial results
        }
      }

      // For "already processed" errors that we couldn't verify, silently continue
      // This is not a real error - the transaction went through
      if (isAlreadyProcessed) {
        console.log(`[OpenChest] Transaction already processed but couldn't verify status. Continuing...`);
        continue; // Try next transaction
      }

      // Other transaction failures - only log error if it's not expected
      if (!isAlreadyProcessed && !isBlockhashError && !isRateLimitError) {
        console.error('Transaction failed:', error);
        captureException(error, {
          ...errorContext,
          stage: 'desktop_send_transaction',
          transactionIndex: i + 1,
          transactionCount: signedTransactions.length,
          completedChests: totalChestsOpened,
          completedSignatures: signatures,
        });
      }

      // If we haven't opened any chests yet, throw the error
      if (totalChestsOpened === 0 && !isAlreadyProcessed) {
        throw error;
      }
      // Otherwise, log but continue to return partial results
      if (!isAlreadyProcessed) {
        console.warn(`[OpenChest] Continuing with partial results after error on batch ${i + 1}/${signedTransactions.length}`);
        break;
      }
    }
    }
  }

  const totalPayoutSol = Number(totalPayout) / 1e9;

  // Final summary log - indicate if partial
  const isPartialSuccess = totalChestsOpened > 0 && totalChestsOpened < chestCount;
  if (isPartialSuccess) {
    console.log(`[OpenChest:PartialSuccess] addr:${addrShort} openedChests:${totalChestsOpened}/${chestCount} totalExtraPoints:${totalTierPoints} totalSolRewards:${totalPayout.toString()} txCount:${signatures.length}`);
  } else {
    console.log(`[OpenChest:Summary] addr:${addrShort} totalChests:${totalChestsOpened} totalExtraPoints:${totalTierPoints} totalSolRewards:${totalPayout.toString()} txCount:${signatures.length}`);
  }

  return {
    signatures,
    chestsOpened: totalChestsOpened,
    rewards: allRewards,
    totalPayout,
    totalPayoutSol,
    tierPointsEarned: totalTierPoints,
    pointRewards: allPointRewards,
  };
}

export function useOpenChest() {
  const { address, isConnected, walletProvider } = useWalletAddress();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: OpenChestInput) => {
      if (!isConnected || !address) {
        throw new Error('Wallet not connected');
      }

      if (!walletProvider) {
        throw new Error('Wallet provider not available');
      }

      return openChest(input.chestCount, walletProvider, input.onProgress);
    },
    onSuccess: () => {
      // Invalidate queries to refresh user data
      queryClient.invalidateQueries({ queryKey: ['userProgress', address] });
      queryClient.invalidateQueries({ queryKey: ['pools'] });
      queryClient.invalidateQueries({ queryKey: ['inviteInfo', address] });
      // Invalidate points-related queries to show updated balance
      queryClient.invalidateQueries({ queryKey: ['userPoints', address] });
      queryClient.invalidateQueries({ queryKey: ['points', 'balance'] });
      queryClient.invalidateQueries({ queryKey: ['pointsLottery'] });
      // Invalidate SOL history to show new transactions
      queryClient.invalidateQueries({ queryKey: ['solHistory', address] });
      // Invalidate Gate campaign to show updated bonus points
      queryClient.invalidateQueries({ queryKey: ['gateCampaign'] });
    },
    onError: (error: Error, input: OpenChestInput) => {
      const errorMsg = error.message?.toLowerCase() || '';
      const originalMsg = error.message || '';

      // Don't show error toast for "already processed" - this means transaction succeeded
      const isAlreadyProcessed = errorMsg.includes('already been processed') ||
                                errorMsg.includes('this transaction has already been processed') ||
                                errorMsg.includes('already processed');
      if (isAlreadyProcessed) {
        console.log('[OpenChest] Transaction already processed - this is not an error');
        return; // Silently handle this
      }

      captureException(error, {
        operation: 'open_chest_fallback',
        chestCount: input.chestCount,
        walletAddress: address,
      });

      // Check for timeout/polling errors - show friendly message with guidance
      const isTimeout = errorMsg.includes('not confirmed after') ||
                       errorMsg.includes('timeout') ||
                       errorMsg.includes('timed out') ||
                       errorMsg.includes('attempts');
      if (isTimeout) {
        toast.error(
          'Transaction is taking longer than expected. Your transaction may still complete — please check your history in a few minutes. If credits were deducted but no rewards appeared, contact support.',
          { duration: 10000 }
        );
        console.error('[OpenChest] Timeout error:', originalMsg);
        return;
      }

      // Check for user rejection/cancellation
      const isRejected = errorMsg.includes('rejected') ||
                        errorMsg.includes('cancelled') ||
                        errorMsg.includes('canceled') ||
                        errorMsg.includes('user rejected') ||
                        errorMsg.includes('user denied');
      if (isRejected) {
        toast.info('Transaction cancelled.', { duration: 3000 });
        return;
      }

      // Don't show error toast for rate limiting - we handle this with retries
      const isRateLimited = errorMsg.includes('429') || errorMsg.includes('too many requests');
      if (isRateLimited) {
        toast.warning('Network is busy. Please try again in a moment.', { duration: 4000 });
        return;
      }

      // Check for network/connection errors
      const isNetworkError = errorMsg.includes('network') ||
                            errorMsg.includes('connection') ||
                            errorMsg.includes('fetch') ||
                            errorMsg.includes('failed to fetch');
      if (isNetworkError) {
        toast.error(
          'Network error. Please check your connection and try again.',
          { duration: 5000 }
        );
        console.error('[OpenChest] Network error:', originalMsg);
        return;
      }

      // Check for blockhash expiration
      const isBlockhashError = errorMsg.includes('blockhash') ||
                              errorMsg.includes('expired') ||
                              errorMsg.includes('block height exceeded');
      if (isBlockhashError) {
        toast.error(
          'Transaction expired. Please try again.',
          { duration: 5000 }
        );
        console.error('[OpenChest] Blockhash error:', originalMsg);
        return;
      }

      // Check if error message contains insufficient balance indicators
      const isInsufficientBalance =
        errorMsg.includes('insufficient') ||
        errorMsg.includes('balance') ||
        errorMsg.includes('panicked at programs/junk-fun-program/src/instructions/open_chest.rs:99') ||
        errorMsg.includes('panicked at programs/junk-fun-program/src/instructions/open_chest.rs:108');

      if (isInsufficientBalance) {
        toast.error(
          'Insufficient Credits. Please burn tokens or deposit Credits first to open chests.',
          { duration: 6000 }
        );
        return;
      }

      // Use parseSolanaError for any remaining cases
      const parsedError = parseSolanaError(error);

      if (isUserActionableError(parsedError)) {
        const description = parsedError.suggestedAction
          ? `${parsedError.message}\n\n${parsedError.suggestedAction}`
          : parsedError.message;

        toast.error('Transaction Failed', {
          description,
          duration: 8000,
        });
        console.error('[OpenChest] Parsed error:', parsedError.type, originalMsg);
        return;
      }

      // Default error - show detailed message in dev, generic in prod
      const displayMessage = import.meta.env.DEV
        ? originalMsg || 'An unexpected error occurred'
        : getGenericErrorMessage(error);

      toast.error(displayMessage, { duration: 5000 });
      console.error('[OpenChest] Unhandled error:', originalMsg);
    },
  });
}
